import type { AgentInfo, NativeBridge, NativeTelemetry } from '@tbcast/shared/native';
import type { PlayerConfig } from './config';

/** Talks to the local TB-cast Agent (Pi/Linux) over HTTP on 127.0.0.1. */
class AgentBridge implements NativeBridge {
  platform: AgentInfo['platform'] = 'linux';
  version = 'unknown';
  constructor(
    private base: string,
    private key: string,
  ) {}

  private async call<T>(
    method: 'GET' | 'POST' | 'PUT',
    path: string,
    body?: unknown,
    timeoutMs = 8000,
  ): Promise<T> {
    const res = await fetch(`${this.base}${path}`, {
      method,
      headers: { 'X-Agent-Key': this.key, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!res.ok) throw new Error(`agent ${path} ${res.status}`);
    return (await res.json()) as T;
  }

  async info() {
    const i = await this.call<AgentInfo>('GET', '/v1/info');
    this.platform = i.platform;
    this.version = i.agentVersion;
    return i;
  }
  telemetry() {
    return this.call<NativeTelemetry>('GET', '/v1/telemetry');
  }
  async screenshot() {
    return (await this.call<{ dataUrl: string | null }>('POST', '/v1/screenshot', {}, 20_000))
      .dataUrl;
  }
  async setDisplay(on: boolean) {
    return (await this.call<{ ok: boolean }>('POST', '/v1/display', { on })).ok;
  }
  async reboot() {
    return (await this.call<{ ok: boolean }>('POST', '/v1/reboot', {})).ok;
  }
  async restartPlayer() {
    return (await this.call<{ ok: boolean }>('POST', '/v1/restart', {})).ok;
  }
  async update() {
    return (await this.call<{ ok: boolean }>('POST', '/v1/update', {}, 60_000)).ok;
  }
  async getToken() {
    return (await this.call<{ token: string | null }>('GET', '/v1/token')).token;
  }
  async setToken(token: string | null) {
    await this.call('PUT', '/v1/token', { token });
  }
}

let bridge: NativeBridge | null | undefined;

/** Electron/Android inject window.tbcastNative; the Pi agent is reached over HTTP. */
export async function getNative(cfg: PlayerConfig): Promise<NativeBridge | null> {
  if (bridge !== undefined) return bridge;
  if (window.tbcastNative) {
    bridge = window.tbcastNative;
    return bridge;
  }
  if (cfg.agentPort && cfg.agentKey) {
    const agent = new AgentBridge(`http://127.0.0.1:${cfg.agentPort}`, cfg.agentKey);
    try {
      await agent.info();
      bridge = agent;
      return bridge;
    } catch {
      // Agent not running (yet): run as a plain browser player, retry on next boot.
    }
  }
  bridge = null;
  return bridge;
}

export function platformName(native: NativeBridge | null): string {
  if (native) return native.platform;
  const ua = navigator.userAgent;
  if (/Tizen|Web0S|webOS|SmartTV|BRAVIA|HbbTV/i.test(ua)) return 'smart-tv';
  if (/Silk|AFT/i.test(ua)) return 'fire-tv';
  if (/Android/i.test(ua)) return 'android-browser';
  if (/CrOS/i.test(ua)) return 'chromeos';
  if (/Windows/i.test(ua)) return 'windows-browser';
  if (/Macintosh/i.test(ua)) return 'macos-browser';
  if (/Linux/i.test(ua)) return 'linux-browser';
  return 'browser';
}
